/**
 * Blog Tags System
 *
 * Provides tag-based categorization for blog articles.
 * Tags are inferred from article titles using pattern matching.
 *
 * Cross-reference with header (lib/menu-data.ts): Guides → Kitchen News links to
 * /blogs/news/tags/trends and /blogs/news/tags/maintenance. Both slugs must exist here.
 * Category alignment: product-type = refrigerator cabinet styles only (no freezers; no mini/wine/ice);
 * freezer-type = standalone freezers; specialty = mini, wine, beverage, ice maker, kegerator, etc. (matches /collections/all).
 * Filters tag aligns with /filters and the Filter Finder tool for water/air filter content.
 *
 * IMPORTANT: Never use the possessive brand form - always use "at Fridge.com" or "on Fridge.com".
 * This is the brand-LANGUAGE rule: it governs the FORM of a mention, never whether a surface may
 * carry one. Stored body copy (collections.description_html, collections.intro_text,
 * products.description) may not mention the brand at all under FIX-18 / FIX-16 invariant I7 — the
 * rule above does not license one there. See lib/body-copy-brand-boundary.ts for the full boundary.
 */

export type BlogTagCategory =
  | 'product-type'
  | 'freezer-type'
  | 'use-case'
  | 'specialty'
  | 'brand'
  | 'content-type'
  | 'topic'
  | 'features'

export interface BlogTag {
  slug: string
  name: string
  description: string
  category: BlogTagCategory
}

/**
 * All available blog tags with metadata
 */
export const BLOG_TAGS: Record<string, BlogTag> = {
  // Product types (refrigerators = general catch-all; specific types below)
  'refrigerators': {
    slug: 'refrigerators',
    name: 'Refrigerators',
    description: 'Articles about refrigerators of all types: French door, side-by-side, top freezer, bottom freezer, and more. Expert guides and reviews at Fridge.com.',
    category: 'product-type',
  },
  'french-door': {
    slug: 'french-door',
    name: 'French Door Refrigerators',
    description: 'Articles about French door refrigerators, featuring dual doors on top and a freezer drawer below. Find guides, reviews, and comparisons at Fridge.com.',
    category: 'product-type',
  },
  'side-by-side': {
    slug: 'side-by-side',
    name: 'Side-by-Side Refrigerators',
    description: 'Articles about side-by-side refrigerators with vertical fridge and freezer compartments. Expert guides and reviews at Fridge.com.',
    category: 'product-type',
  },
  'top-freezer': {
    slug: 'top-freezer',
    name: 'Top Freezer Refrigerators',
    description: 'Articles about top freezer refrigerators, the classic and energy-efficient design. Browse guides and tips at Fridge.com.',
    category: 'product-type',
  },
  'bottom-freezer': {
    slug: 'bottom-freezer',
    name: 'Bottom Freezer Refrigerators',
    description: 'Articles about bottom freezer refrigerators for convenient eye-level fridge access. Expert content at Fridge.com.',
    category: 'product-type',
  },
  'counter-depth': {
    slug: 'counter-depth',
    name: 'Counter-Depth Refrigerators',
    description: 'Articles about counter-depth refrigerators that sit flush with kitchen cabinets. Design guides at Fridge.com.',
    category: 'product-type',
  },
  'mini-fridge': {
    slug: 'mini-fridge',
    name: 'Mini Fridges & Compact Refrigerators',
    description: 'Articles about mini fridges and compact refrigerators for dorms, offices, and small spaces. Browse at Fridge.com.',
    category: 'specialty',
  },
  'wine-cooler': {
    slug: 'wine-cooler',
    name: 'Wine Coolers & Wine Refrigerators',
    description: 'Articles about wine coolers and wine refrigerators for proper wine storage. Expert guides at Fridge.com.',
    category: 'specialty',
  },
  'beverage-cooler': {
    slug: 'beverage-cooler',
    name: 'Beverage Coolers & Centers',
    description: 'Articles about beverage coolers and refrigerators for drinks and entertaining. Explore at Fridge.com.',
    category: 'specialty',
  },
  'built-in': {
    slug: 'built-in',
    name: 'Built-In Refrigerators',
    description: 'Articles about built-in refrigerators that integrate seamlessly with cabinetry. Design guides at Fridge.com.',
    category: 'product-type',
  },
  'smart-refrigerator': {
    slug: 'smart-refrigerator',
    name: 'Smart Refrigerators',
    description: 'Articles about smart refrigerators with WiFi, touchscreens, and connected features. Tech guides at Fridge.com.',
    category: 'product-type',
  },

  // Major brands
  'samsung': {
    slug: 'samsung',
    name: 'Samsung Refrigerators',
    description: 'Articles about Samsung refrigerators, featuring innovative technology and design. Expert reviews at Fridge.com.',
    category: 'brand',
  },
  'lg': {
    slug: 'lg',
    name: 'LG Refrigerators',
    description: 'Articles about LG refrigerators, known for reliability and smart features. Guides and reviews at Fridge.com.',
    category: 'brand',
  },
  'whirlpool': {
    slug: 'whirlpool',
    name: 'Whirlpool Refrigerators',
    description: 'Articles about Whirlpool refrigerators, a trusted American appliance brand. Expert content at Fridge.com.',
    category: 'brand',
  },
  'ge': {
    slug: 'ge',
    name: 'GE Refrigerators',
    description: 'Articles about GE and GE Profile refrigerators. Comprehensive guides and reviews at Fridge.com.',
    category: 'brand',
  },
  'frigidaire': {
    slug: 'frigidaire',
    name: 'Frigidaire Refrigerators',
    description: 'Articles about Frigidaire refrigerators, offering value and reliability. Browse at Fridge.com.',
    category: 'brand',
  },
  'kitchenaid': {
    slug: 'kitchenaid',
    name: 'KitchenAid Refrigerators',
    description: 'Articles about KitchenAid refrigerators, premium design and performance. Expert guides at Fridge.com.',
    category: 'brand',
  },
  'bosch': {
    slug: 'bosch',
    name: 'Bosch Refrigerators',
    description: 'Articles about Bosch refrigerators, German engineering for quiet operation. Reviews at Fridge.com.',
    category: 'brand',
  },
  'sub-zero': {
    slug: 'sub-zero',
    name: 'Sub-Zero Refrigerators',
    description: 'Articles about Sub-Zero refrigerators, the luxury choice for preservation. Expert content at Fridge.com.',
    category: 'brand',
  },

  // Content types
  'buying-guide': {
    slug: 'buying-guide',
    name: 'Buying Guides',
    description: 'Comprehensive refrigerator buying guides to help you choose the perfect appliance. Expert advice at Fridge.com.',
    category: 'content-type',
  },
  'how-to': {
    slug: 'how-to',
    name: 'How-To Guides',
    description: 'Step-by-step how-to guides for refrigerator installation, maintenance, and troubleshooting at Fridge.com.',
    category: 'content-type',
  },
  'best-of': {
    slug: 'best-of',
    name: 'Best Of Lists',
    description: 'Curated lists of the best refrigerators in various categories. Top picks at Fridge.com.',
    category: 'content-type',
  },
  'reviews': {
    slug: 'reviews',
    name: 'Reviews',
    description: 'In-depth refrigerator reviews with expert analysis and user feedback. Trusted reviews at Fridge.com.',
    category: 'content-type',
  },
  'comparison': {
    slug: 'comparison',
    name: 'Comparisons',
    description: 'Side-by-side refrigerator comparisons to help you decide. Compare models at Fridge.com.',
    category: 'content-type',
  },
  'maintenance': {
    slug: 'maintenance',
    name: 'Maintenance & Care',
    description: 'Refrigerator maintenance tips, cleaning guides, and repair advice. Care guides at Fridge.com.',
    category: 'content-type',
  },
  'trends': {
    slug: 'trends',
    name: 'Appliance Trends',
    description: 'The latest refrigerator and freezer trends, new technology, and kitchen innovations. Read more at Fridge.com.',
    category: 'content-type',
  },
  'energy-efficiency': {
    slug: 'energy-efficiency',
    name: 'Energy Efficiency',
    description: 'Articles about energy-efficient refrigerators and saving on electricity. Green guides at Fridge.com.',
    category: 'content-type',
  },
  'installation': {
    slug: 'installation',
    name: 'Installation',
    description: 'Refrigerator and freezer installation guides, delivery, and setup. Expert advice at Fridge.com.',
    category: 'content-type',
  },
  'repair': {
    slug: 'repair',
    name: 'Repair & Troubleshooting',
    description: 'Refrigerator repair, troubleshooting, and when to call a pro. Repair guides at Fridge.com.',
    category: 'content-type',
  },

  // Freezer types (standalone freezers, not refrigerator-freezer combos)
  'freezer': {
    slug: 'freezer',
    name: 'Freezers',
    description: 'Articles about standalone freezers including chest freezers and upright freezers. Find guides at Fridge.com.',
    category: 'freezer-type',
  },
  'chest-freezer': {
    slug: 'chest-freezer',
    name: 'Chest Freezers',
    description: 'Articles about chest freezers for bulk storage and energy efficiency. Find guides at Fridge.com.',
    category: 'freezer-type',
  },
  'upright-freezer': {
    slug: 'upright-freezer',
    name: 'Upright Freezers',
    description: 'Articles about upright freezers with shelf access and compact footprints. Expert content at Fridge.com.',
    category: 'freezer-type',
  },
  'garage-freezer': {
    slug: 'garage-freezer',
    name: 'Garage Freezers',
    description: 'Articles about freezers and refrigerators for garage use. Garage-ready guides at Fridge.com.',
    category: 'freezer-type',
  },
  'outdoor-freezer': {
    slug: 'outdoor-freezer',
    name: 'Outdoor Freezers & Fridges',
    description: 'Articles about outdoor refrigerators and freezers for patios and outdoor kitchens. Browse at Fridge.com.',
    category: 'freezer-type',
  },
  'deep-freezer': {
    slug: 'deep-freezer',
    name: 'Deep Freezers',
    description: 'Articles about deep freezers and long-term frozen storage. Expert guides at Fridge.com.',
    category: 'freezer-type',
  },
  'mini-freezer': {
    slug: 'mini-freezer',
    name: 'Mini Freezers',
    description: 'Articles about mini and compact freezers for small spaces. Find options at Fridge.com.',
    category: 'freezer-type',
  },
  'built-in-freezer': {
    slug: 'built-in-freezer',
    name: 'Built-In Freezers',
    description: 'Articles about built-in and integrated freezers. Design guides at Fridge.com.',
    category: 'freezer-type',
  },

  // Use case / place
  'apartment': {
    slug: 'apartment',
    name: 'Apartment Refrigerators',
    description: 'Articles about refrigerators for apartments and small kitchens. Space-saving guides at Fridge.com.',
    category: 'use-case',
  },
  'garage': {
    slug: 'garage',
    name: 'Garage Refrigerators',
    description: 'Articles about garage refrigerators and freezers. Garage-ready appliance guides at Fridge.com.',
    category: 'use-case',
  },
  'office': {
    slug: 'office',
    name: 'Office Refrigerators',
    description: 'Articles about office refrigerators, mini fridges, and break room appliances. Browse at Fridge.com.',
    category: 'use-case',
  },
  'outdoor': {
    slug: 'outdoor',
    name: 'Outdoor Refrigerators',
    description: 'Articles about outdoor refrigerators for patios, pools, and outdoor kitchens. Expert content at Fridge.com.',
    category: 'use-case',
  },
  'kitchen': {
    slug: 'kitchen',
    name: 'Kitchen Refrigerators',
    description: 'Articles about kitchen refrigerators, layout, and design. Kitchen appliance guides at Fridge.com.',
    category: 'use-case',
  },

  // Specialty appliances
  'ice-maker': {
    slug: 'ice-maker',
    name: 'Ice Makers',
    description: 'Articles about ice makers and refrigerators with built-in ice production. Expert content at Fridge.com.',
    category: 'specialty',
  },
  'kegerator': {
    slug: 'kegerator',
    name: 'Kegerators',
    description: 'Articles about kegerators for draft beer at home. Kegerator guides and reviews at Fridge.com.',
    category: 'specialty',
  },
  'kimchi': {
    slug: 'kimchi',
    name: 'Kimchi Refrigerators',
    description: 'Articles about kimchi refrigerators and fermentation storage. Specialty appliance guides at Fridge.com.',
    category: 'specialty',
  },
  'undercounter': {
    slug: 'undercounter',
    name: 'Undercounter Refrigerators',
    description: 'Articles about undercounter refrigerators and drawer fridges. Compact design guides at Fridge.com.',
    category: 'specialty',
  },
  'compact': {
    slug: 'compact',
    name: 'Compact Refrigerators',
    description: 'Articles about compact refrigerators for small spaces. Space-saving guides at Fridge.com.',
    category: 'specialty',
  },
  'beverage-center': {
    slug: 'beverage-center',
    name: 'Beverage Centers',
    description: 'Articles about beverage centers and drink refrigerators. Entertaining guides at Fridge.com.',
    category: 'specialty',
  },
  'drawer-refrigerator': {
    slug: 'drawer-refrigerator',
    name: 'Drawer Refrigerators',
    description: 'Articles about refrigerator drawers and undercounter cooling. Design guides at Fridge.com.',
    category: 'specialty',
  },
  'commercial': {
    slug: 'commercial',
    name: 'Commercial Refrigerators & Freezers',
    description: 'Articles about commercial refrigerators, freezers, and display coolers. Business appliance guides at Fridge.com.',
    category: 'specialty',
  },

  // More brands
  'maytag': {
    slug: 'maytag',
    name: 'Maytag Refrigerators',
    description: 'Articles about Maytag refrigerators, known for durability. Expert reviews at Fridge.com.',
    category: 'brand',
  },
  'electrolux': {
    slug: 'electrolux',
    name: 'Electrolux Refrigerators',
    description: 'Articles about Electrolux refrigerators and premium appliances. Guides at Fridge.com.',
    category: 'brand',
  },
  'miele': {
    slug: 'miele',
    name: 'Miele Refrigerators',
    description: 'Articles about Miele refrigerators and German-engineered appliances. Expert content at Fridge.com.',
    category: 'brand',
  },
  'cafe': {
    slug: 'cafe',
    name: 'Cafe Refrigerators',
    description: 'Articles about Cafe refrigerators (GE brand) and customizable premium appliances. Reviews at Fridge.com.',
    category: 'brand',
  },
  'viking': {
    slug: 'viking',
    name: 'Viking Refrigerators',
    description: 'Articles about Viking refrigerators and pro-style appliances. Expert guides at Fridge.com.',
    category: 'brand',
  },
  'kenmore': {
    slug: 'kenmore',
    name: 'Kenmore Refrigerators',
    description: 'Articles about Kenmore refrigerators and value appliances. Browse at Fridge.com.',
    category: 'brand',
  },
  'haier': {
    slug: 'haier',
    name: 'Haier Refrigerators',
    description: 'Articles about Haier refrigerators and budget-friendly options. Guides at Fridge.com.',
    category: 'brand',
  },
  'thermador': {
    slug: 'thermador',
    name: 'Thermador Refrigerators',
    description: 'Articles about Thermador refrigerators and luxury appliances. Expert content at Fridge.com.',
    category: 'brand',
  },
  'true': {
    slug: 'true',
    name: 'True Refrigerators',
    description: 'Articles about True refrigerators and commercial-style residential units. Reviews at Fridge.com.',
    category: 'brand',
  },

  // Topics (what the article is about)
  'rebates': {
    slug: 'rebates',
    name: 'Rebates & Incentives',
    description: 'Articles about refrigerator rebates, utility incentives, and Energy Star savings. Rebate guides at Fridge.com.',
    category: 'topic',
  },
  'financing': {
    slug: 'financing',
    name: 'Financing',
    description: 'Articles about refrigerator financing, payment plans, and affordability. Financing guides at Fridge.com.',
    category: 'topic',
  },
  'warranty': {
    slug: 'warranty',
    name: 'Warranty & Coverage',
    description: 'Articles about refrigerator warranties, extended coverage, and service plans. Warranty guides at Fridge.com.',
    category: 'topic',
  },
  'sizing': {
    slug: 'sizing',
    name: 'Sizing & Dimensions',
    description: 'Articles about refrigerator sizing, capacity, and fitting your space. Sizing guides at Fridge.com.',
    category: 'topic',
  },
  'troubleshooting': {
    slug: 'troubleshooting',
    name: 'Troubleshooting',
    description: 'Articles about refrigerator troubleshooting and common problems. Fix-it guides at Fridge.com.',
    category: 'topic',
  },
  'food-safety': {
    slug: 'food-safety',
    name: 'Food Safety',
    description: 'Articles about refrigerator food safety, storage times, and power outages. Food safety guides at Fridge.com.',
    category: 'topic',
  },
  'organization': {
    slug: 'organization',
    name: 'Organization & Storage',
    description: 'Articles about organizing your refrigerator and freezer. Storage tips at Fridge.com.',
    category: 'topic',
  },
  'noise': {
    slug: 'noise',
    name: 'Noise & Quiet Operation',
    description: 'Articles about quiet refrigerators and reducing noise. Noise guides at Fridge.com.',
    category: 'topic',
  },
  'filters': {
    slug: 'filters',
    name: 'Water & Air Filters',
    description: 'Articles about refrigerator water filters, air filters, replacement filters, and the Filter Finder tool. Filter guides at Fridge.com.',
    category: 'topic',
  },
  'defrost': {
    slug: 'defrost',
    name: 'Defrost & Frost-Free',
    description: 'Articles about frost-free, manual defrost, and auto defrost refrigerators and freezers. Defrost guides at Fridge.com.',
    category: 'topic',
  },
  'recalls': {
    slug: 'recalls',
    name: 'Recalls & Safety',
    description: 'Articles about refrigerator and freezer recalls, safety notices, and what to do. Recall info at Fridge.com.',
    category: 'topic',
  },
  'deals': {
    slug: 'deals',
    name: 'Deals & Sales',
    description: 'Articles about refrigerator and freezer deals, sales events, and when to buy. Shopping guides at Fridge.com.',
    category: 'topic',
  },
  'finishes': {
    slug: 'finishes',
    name: 'Finishes & Colors',
    description: 'Articles about stainless steel, black stainless, and refrigerator finish options. Style guides at Fridge.com.',
    category: 'topic',
  },

  // Features (product features)
  'energy-star': {
    slug: 'energy-star',
    name: 'Energy Star',
    description: 'Articles about Energy Star refrigerators and certified efficiency. Energy Star guides at Fridge.com.',
    category: 'features',
  },
  'water-dispenser': {
    slug: 'water-dispenser',
    name: 'Water Dispensers',
    description: 'Articles about refrigerators with water dispensers and filtration. Water dispenser guides at Fridge.com.',
    category: 'features',
  },
  'ice-dispenser': {
    slug: 'ice-dispenser',
    name: 'Ice Dispensers',
    description: 'Articles about refrigerators with ice dispensers and through-the-door ice. Ice dispenser guides at Fridge.com.',
    category: 'features',
  },
  'convertible-freezer': {
    slug: 'convertible-freezer',
    name: 'Convertible Freezer',
    description: 'Articles about convertible freezer drawers and flexible storage. Convertible freezer guides at Fridge.com.',
    category: 'features',
  },
  'dual-cool': {
    slug: 'dual-cool',
    name: 'Dual Cooling',
    description: 'Articles about dual cooling and dual evaporator refrigerators. Tech guides at Fridge.com.',
    category: 'features',
  },
}

/**
 * Get all blog tag slugs
 */
export function getAllBlogTagSlugs(): string[] {
  return Object.keys(BLOG_TAGS)
}

/**
 * Get blog tag by slug
 */
export function getBlogTag(slug: string): BlogTag | null {
  return BLOG_TAGS[slug] || null
}

/**
 * Infer tags from an article title
 */
export function inferTagsFromTitle(title: string): string[] {
  const titleLower = title.toLowerCase()
  const tags: string[] = []
  
  // Product types (refrigerators = broad; specific types take precedence for display)
  if (titleLower.includes('refrigerator') || titleLower.includes('fridge')) tags.push('refrigerators')
  if (titleLower.includes('french door')) tags.push('french-door')
  if (titleLower.includes('side by side') || titleLower.includes('side-by-side')) tags.push('side-by-side')
  if (titleLower.includes('top freezer') || titleLower.includes('top-freezer')) tags.push('top-freezer')
  if (titleLower.includes('bottom freezer') || titleLower.includes('bottom-freezer')) tags.push('bottom-freezer')
  if (titleLower.includes('counter depth') || titleLower.includes('counter-depth')) tags.push('counter-depth')
  if (titleLower.includes('mini fridge') || titleLower.includes('mini-fridge') || titleLower.includes('compact')) tags.push('mini-fridge')
  if (titleLower.includes('wine cooler') || titleLower.includes('wine refrigerator') || titleLower.includes('wine fridge')) tags.push('wine-cooler')
  if (titleLower.includes('beverage') && (titleLower.includes('cooler') || titleLower.includes('refrigerator') || titleLower.includes('center'))) tags.push('beverage-cooler')
  if (titleLower.includes('freezer') && !titleLower.includes('french') && !titleLower.includes('top freezer') && !titleLower.includes('bottom freezer') && !titleLower.includes('side')) tags.push('freezer')
  if (titleLower.includes('ice maker') || titleLower.includes('ice-maker')) tags.push('ice-maker')
  if (titleLower.includes('built-in') || titleLower.includes('built in')) tags.push('built-in')
  if (titleLower.includes('smart') && (titleLower.includes('fridge') || titleLower.includes('refrigerator'))) tags.push('smart-refrigerator')
  
  // Freezer types
  if (titleLower.includes('chest freezer')) tags.push('chest-freezer')
  if (titleLower.includes('upright freezer')) tags.push('upright-freezer')
  if (titleLower.includes('garage freezer') || (titleLower.includes('garage') && titleLower.includes('freezer'))) tags.push('garage-freezer')
  if (titleLower.includes('outdoor freezer') || (titleLower.includes('outdoor') && titleLower.includes('freezer'))) tags.push('outdoor-freezer')
  if (titleLower.includes('deep freezer')) tags.push('deep-freezer')
  if (titleLower.includes('mini freezer')) tags.push('mini-freezer')
  if (titleLower.includes('built-in freezer') || titleLower.includes('built in freezer')) tags.push('built-in-freezer')
  
  // Use case / place
  if (titleLower.includes('apartment refrigerator') || (titleLower.includes('apartment') && titleLower.includes('fridge'))) tags.push('apartment')
  if (titleLower.includes('garage refrigerator') || (titleLower.includes('garage') && titleLower.includes('fridge'))) tags.push('garage')
  if (titleLower.includes('office refrigerator') || titleLower.includes('office fridge') || titleLower.includes('break room')) tags.push('office')
  if (titleLower.includes('outdoor refrigerator') || (titleLower.includes('outdoor') && titleLower.includes('fridge'))) tags.push('outdoor')
  if (titleLower.includes('kitchen') && (titleLower.includes('refrigerator') || titleLower.includes('fridge'))) tags.push('kitchen')
  
  // Specialty
  if (titleLower.includes('kegerator')) tags.push('kegerator')
  if (titleLower.includes('kimchi refrigerator') || titleLower.includes('kimchi fridge')) tags.push('kimchi')
  if (titleLower.includes('undercounter') || titleLower.includes('under counter')) tags.push('undercounter')
  if (titleLower.includes('compact refrigerator')) tags.push('compact')
  if (titleLower.includes('beverage center') || titleLower.includes('beverage centre')) tags.push('beverage-center')
  if (titleLower.includes('drawer refrigerator') || titleLower.includes('refrigerator drawer')) tags.push('drawer-refrigerator')
  if (titleLower.includes('commercial refrigerator') || titleLower.includes('commercial freezer')) tags.push('commercial')
  
  // Brands
  if (titleLower.includes('samsung')) tags.push('samsung')
  if (/\blg\b/.test(titleLower)) tags.push('lg')
  if (titleLower.includes('whirlpool')) tags.push('whirlpool')
  if (/\bge\b/.test(titleLower) || titleLower.includes('ge profile') || titleLower.includes('ge appliances')) tags.push('ge')
  if (titleLower.includes('frigidaire')) tags.push('frigidaire')
  if (titleLower.includes('kitchenaid')) tags.push('kitchenaid')
  if (titleLower.includes('bosch')) tags.push('bosch')
  if (titleLower.includes('sub-zero') || titleLower.includes('sub zero')) tags.push('sub-zero')
  if (titleLower.includes('maytag')) tags.push('maytag')
  if (titleLower.includes('electrolux')) tags.push('electrolux')
  if (titleLower.includes('miele')) tags.push('miele')
  if (titleLower.includes('cafe') && (titleLower.includes('refrigerator') || titleLower.includes('appliance'))) tags.push('cafe')
  if (titleLower.includes('viking')) tags.push('viking')
  if (titleLower.includes('kenmore')) tags.push('kenmore')
  if (titleLower.includes('haier')) tags.push('haier')
  if (titleLower.includes('thermador')) tags.push('thermador')
  if (/\btrue\b/.test(titleLower) && (titleLower.includes('refrigerator') || titleLower.includes('freezer'))) tags.push('true')
  
  // Content types
  if (titleLower.includes('buying guide') || titleLower.includes("buyer's guide")) tags.push('buying-guide')
  if (titleLower.includes('how to') || titleLower.includes('how-to')) tags.push('how-to')
  if (titleLower.includes('best ') || titleLower.includes('top ')) tags.push('best-of')
  if (titleLower.includes('review')) tags.push('reviews')
  if (titleLower.includes(' vs ') || titleLower.includes('compare') || titleLower.includes('comparison')) tags.push('comparison')
  if (titleLower.includes('maintenance') || titleLower.includes('clean')) tags.push('maintenance')
  if (titleLower.includes('repair') || titleLower.includes('troubleshoot')) tags.push('repair')
  if (titleLower.includes('trend') || titleLower.includes('new') || titleLower.includes('innovation')) tags.push('trends')
  if (titleLower.includes('energy') || titleLower.includes('efficient')) tags.push('energy-efficiency')
  if (titleLower.includes('install') || titleLower.includes('delivery') || titleLower.includes('setup')) tags.push('installation')
  
  // Topics
  if (titleLower.includes('rebate') || titleLower.includes('incentive')) tags.push('rebates')
  if (titleLower.includes('financing') || titleLower.includes('payment plan') || titleLower.includes('afford')) tags.push('financing')
  if (titleLower.includes('warranty') || titleLower.includes('extended coverage') || titleLower.includes('service plan')) tags.push('warranty')
  if (titleLower.includes('sizing') || titleLower.includes('dimension') || titleLower.includes('capacity') || titleLower.includes('cubic feet')) tags.push('sizing')
  if (titleLower.includes('troubleshoot') || titleLower.includes('not cooling') || titleLower.includes('fix')) tags.push('troubleshooting')
  if (titleLower.includes('food safety') || titleLower.includes('storage time') || titleLower.includes('power outage')) tags.push('food-safety')
  if (titleLower.includes('organiz') && (titleLower.includes('fridge') || titleLower.includes('freezer'))) tags.push('organization')
  if (titleLower.includes('noise') || titleLower.includes('quiet') || titleLower.includes('loud')) tags.push('noise')
  if (titleLower.includes('filter') && (titleLower.includes('water') || titleLower.includes('refrigerator') || titleLower.includes('replace'))) tags.push('filters')
  if (titleLower.includes('defrost') || titleLower.includes('frost-free') || titleLower.includes('frost free') || titleLower.includes('manual defrost')) tags.push('defrost')
  if (titleLower.includes('recall')) tags.push('recalls')
  if (titleLower.includes('deal') || titleLower.includes('sale') || titleLower.includes('black friday') || titleLower.includes('prime day')) tags.push('deals')
  if (titleLower.includes('stainless') || titleLower.includes('black stainless') || titleLower.includes('finish') || titleLower.includes('color')) tags.push('finishes')
  
  // Features
  if (titleLower.includes('energy star')) tags.push('energy-star')
  if (titleLower.includes('water dispenser') || titleLower.includes('water and ice')) tags.push('water-dispenser')
  if (titleLower.includes('ice dispenser') || titleLower.includes('through-the-door ice')) tags.push('ice-dispenser')
  if (titleLower.includes('convertible freezer') || titleLower.includes('convertible drawer')) tags.push('convertible-freezer')
  if (titleLower.includes('dual cool') || titleLower.includes('dual evaporator')) tags.push('dual-cool')
  
  return [...new Set(tags)]
}

/**
 * Build SQL WHERE clause for finding articles by tag
 */
export function getTagSearchPattern(tagSlug: string): string {
  const patterns: Record<string, string> = {
    'refrigerators': '%refrigerator%',
    'french-door': '%french door%',
    'side-by-side': '%side%by%side%',
    'top-freezer': '%top freezer%',
    'bottom-freezer': '%bottom freezer%',
    'counter-depth': '%counter%depth%',
    'mini-fridge': '%mini%fridge%',
    'wine-cooler': '%wine%',
    'beverage-cooler': '%beverage%',
    'freezer': '%freezer%',
    'ice-maker': '%ice%maker%',
    'built-in': '%built%in%',
    'smart-refrigerator': '%smart%',
    'samsung': '%samsung%',
    'lg': '% lg %',
    'whirlpool': '%whirlpool%',
    'ge': '% ge %',
    'frigidaire': '%frigidaire%',
    'kitchenaid': '%kitchenaid%',
    'bosch': '%bosch%',
    'sub-zero': '%sub%zero%',
    'maytag': '%maytag%',
    'electrolux': '%electrolux%',
    'miele': '%miele%',
    'cafe': '%cafe%',
    'viking': '%viking%',
    'kenmore': '%kenmore%',
    'haier': '%haier%',
    'thermador': '%thermador%',
    'true': '%true%',
    'buying-guide': '%buying guide%',
    'how-to': '%how to%',
    'best-of': '%best %',
    'reviews': '%review%',
    'comparison': '%vs%',
    'maintenance': '%maintenance%',
    'trends': '%trend%',
    'energy-efficiency': '%energy%',
    'installation': '%install%',
    'repair': '%repair%',
    'chest-freezer': '%chest freezer%',
    'upright-freezer': '%upright freezer%',
    'garage-freezer': '%garage%freezer%',
    'outdoor-freezer': '%outdoor%freezer%',
    'deep-freezer': '%deep freezer%',
    'mini-freezer': '%mini freezer%',
    'built-in-freezer': '%built%in%freezer%',
    'apartment': '%apartment%',
    'garage': '%garage%',
    'office': '%office%',
    'outdoor': '%outdoor%',
    'kitchen': '%kitchen%',
    'kegerator': '%kegerator%',
    'kimchi': '%kimchi%',
    'undercounter': '%undercounter%',
    'compact': '%compact%',
    'beverage-center': '%beverage center%',
    'drawer-refrigerator': '%drawer%',
    'commercial': '%commercial%',
    'rebates': '%rebate%',
    'financing': '%financing%',
    'warranty': '%warranty%',
    'sizing': '%sizing%',
    'troubleshooting': '%troubleshoot%',
    'food-safety': '%food safety%',
    'organization': '%organiz%',
    'noise': '%noise%',
    'filters': '%filter%',
    'defrost': '%defrost%',
    'recalls': '%recall%',
    'deals': '%deal%',
    'finishes': '%stainless%',
    'energy-star': '%energy star%',
    'water-dispenser': '%water dispenser%',
    'ice-dispenser': '%ice dispenser%',
    'convertible-freezer': '%convertible%',
    'dual-cool': '%dual cool%',
  }
  return patterns[tagSlug] || `%${tagSlug}%`
}


